import { useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Card } from '@actual-app/components/card';
import { View } from '@actual-app/components/view';

import type {
  BudgetCycleUpdateRequest,
  FinancialManagementState,
  ProductFinanceClient,
} from '#components/product-finance/client';

import type { ManagedIncome } from './IncomeManagement';
import { getManagementErrorMessage } from './management-errors';
import {
  buttonRowStyle,
  errorStyle,
  Field,
  formStyle,
  helpStyle,
  inputStyle,
  ManagementCard,
} from './management-shared';

export type ManagedCycle = FinancialManagementState['budgetCycles'][number];

export function CycleManagement({
  state,
  client,
  onChanged,
}: Readonly<{
  state: FinancialManagementState;
  client: ProductFinanceClient;
  onChanged: () => Promise<void>;
}>) {
  const [editing, setEditing] = useState<ManagedCycle | null>(null);
  return (
    <View style={{ gap: 12 }}>
      <ManagementCard title={<Trans>Budget cycles</Trans>}>
        <p style={helpStyle}>
          <Trans>
            A cycle assigns expenses to the income occurrence that funds them.
          </Trans>
        </p>
      </ManagementCard>
      {editing !== null && (
        <CycleForm
          key={editing.id}
          cycle={editing}
          incomes={state.incomeTemplates}
          client={client}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onChanged();
          }}
        />
      )}
      {state.budgetCycles.map(cycle => (
        <CycleCard
          key={cycle.id}
          cycle={cycle}
          anchorName={
            state.incomeTemplates.find(
              income => income.id === cycle.anchorIncomeTemplateId,
            )?.name
          }
          client={client}
          onEdit={() => setEditing(cycle)}
          onChanged={onChanged}
        />
      ))}
    </View>
  );
}

export function CycleCard({
  cycle,
  anchorName,
  client,
  onEdit,
  onChanged,
}: Readonly<{
  cycle: ManagedCycle;
  anchorName?: string;
  client: ProductFinanceClient;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}>) {
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  async function toggle() {
    setSubmitting(true);
    setError(false);
    try {
      await client.setBudgetCycleEnabled({
        id: cycle.id,
        enabled: !cycle.enabled,
      });
      await onChanged();
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <Card style={{ margin: 0 }}>
      <View style={{ padding: 16, gap: 8 }}>
        <strong>{cycle.name}</strong>
        <span style={helpStyle}>
          {anchorName ?? <Trans>Income source unavailable</Trans>} ·{' '}
          {cycle.enabled ? <Trans>Enabled</Trans> : <Trans>Disabled</Trans>}
        </span>
        {error && (
          <p role="alert" style={errorStyle}>
            <Trans>
              Disable assigned expenses or choose a valid anchor first.
            </Trans>
          </p>
        )}
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Button onPress={onEdit}>
            <Trans>Edit</Trans>
          </Button>
          <ButtonWithLoading
            isLoading={isSubmitting}
            onPress={() => void toggle()}
          >
            {cycle.enabled ? <Trans>Disable</Trans> : <Trans>Enable</Trans>}
          </ButtonWithLoading>
        </View>
      </View>
    </Card>
  );
}

export function CycleForm({
  cycle,
  incomes,
  client,
  onCancel,
  onSaved,
}: Readonly<{
  cycle: ManagedCycle;
  incomes: readonly ManagedIncome[];
  client: ProductFinanceClient;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}>) {
  const { t } = useTranslation();
  const [name, setName] = useState(cycle.name);
  const [anchorIncomeTemplateId, setAnchor] = useState(
    cycle.anchorIncomeTemplateId,
  );
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const input: BudgetCycleUpdateRequest = {
      id: cycle.id,
      name,
      anchorIncomeTemplateId,
      enabled: cycle.enabled,
    };
    setSubmitting(true);
    setError(null);
    try {
      await client.updateBudgetCycle(input);
      await onSaved();
    } catch (cause) {
      setError(getManagementErrorMessage(cause, t));
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <ManagementCard title={<Trans>Edit budget cycle</Trans>}>
      <form onSubmit={submit} style={formStyle}>
        <Field label={<Trans>Name</Trans>} htmlFor="cycle-name">
          <input
            id="cycle-name"
            value={name}
            onChange={event => setName(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label={<Trans>Funding income</Trans>} htmlFor="cycle-anchor">
          <select
            id="cycle-anchor"
            value={anchorIncomeTemplateId}
            onChange={event => setAnchor(event.target.value)}
            style={inputStyle}
          >
            {incomes.map(income => (
              <option key={income.id} value={income.id}>
                {income.name}
              </option>
            ))}
          </select>
        </Field>
        {error !== null && (
          <p role="alert" style={errorStyle}>
            {error}
          </p>
        )}
        <View style={buttonRowStyle}>
          <Button type="button" onPress={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <ButtonWithLoading
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            <Trans>Save cycle</Trans>
          </ButtonWithLoading>
        </View>
      </form>
    </ManagementCard>
  );
}
